import { ChangeEvent, SyntheticEvent, useState } from "react";

import { AxiosError } from "axios";
import { ERROR_CODES, USER_INFO_LENGTHS } from "@/shared";
import { useLoginDataStore } from "@/stores/useLoginDataStore";

import axios from "../../../api/axios";
import { ApiResources } from "../../../api/resources";
import useTwoFA from "../../../components/Navbar/components/Settings/hooks/useTwoFA";
import { AUTHENTICATION_TYPES } from "../types";
import { useAuth } from "./../../../context/Auth/AuthProvider";
import useLogin from "./useLogin";
import { useValidateOtp } from "./useValidateOtp";

export const useTwoFactorVerification = ({ ...props }) => {
  const { twoFaMethod } = useAuth();
  const { storeTokensAndNavigateToHomepage } = useLogin();
  const { handleTwoFaError } = useTwoFA({});

  const setIsLoginState = useLoginDataStore(state => state.setIsLoginState);

  const [code, setCode] = useState("");

  const { error, setError, validateOtp } = useValidateOtp();

  const { setIsLoading, setIsButtonLoading, token, userName } = props;

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newValue = event.target.value;

    const trimmedValue = String(newValue.trim());

    if (!trimmedValue) {
      setError(null);
    }

    validateOtp(event);

    if (trimmedValue.length <= USER_INFO_LENGTHS.OtpMax) {
      setCode(trimmedValue);
    }
  };

  const handleSubmit = async (
    e: SyntheticEvent,
    navigateToLogin: () => void
  ) => {
    e.preventDefault();

    const authenticatorMethod =
      twoFaMethod === AUTHENTICATION_TYPES.Authenticator;

    const authenticatorPayload = {
      otpCode: code
    };

    const emailPayload = {
      email: userName,
      otp: code
    };

    const payload = authenticatorMethod ? authenticatorPayload : emailPayload;

    const resource = authenticatorMethod
      ? ApiResources.CreateTwoFactorToken
      : ApiResources.VerifyEmailOtp;

    try {
      // set login state to display auth loader on Users/Me request
      setIsLoginState(true);
      setIsLoading(true);
      setIsButtonLoading(true);
      const response = await axios.post(
        resource,
        payload,
        // This endpoint requires to send authorization token.
        // Tokens are not stored yet into local storage, so that user can't access the app without 2FA
        // (that is why useAxiosPrivate() is not used here).
        // The token is sent in the header here, by using axios 'headers' object.
        {
          headers: {
            Authorization: "Bearer " + token
          }
        }
      );

      storeTokensAndNavigateToHomepage(response);
    } catch (error) {
      const err = error as AxiosError;

      if (err.response?.status === ERROR_CODES.Unauthorized) {
        handleTwoFaError(error as AxiosError, setError);
        return navigateToLogin();
      }

      handleTwoFaError(error as AxiosError, setError);
    } finally {
      setIsLoading(false);
      setIsButtonLoading(false);
    }
  };

  return {
    otpCode: code,
    twoFaError: error,
    setTwoFaError: setError,
    handleOtpChange: handleChange,
    handle2FaSubmit: handleSubmit,
    setCode,
    validateOtp
  };
};
